import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, ACTIVE_ORG_STORAGE_KEY } from '@/core/api';
import type { MeResponse, UserProfile } from '@/types/api';

function resolveActiveOrgId(me: MeResponse): number | null {
  const fromUser = me.user.organization_id ?? me.user.organization_ids?.[0];
  if (fromUser != null) return fromUser;
  return me.organizations.find((org) => org.id != null)?.id ?? null;
}

export default function SetupPage() {
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingExistingOrg, setCheckingExistingOrg] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setCheckingExistingOrg(true);
    setError('');
    apiClient<MeResponse>('/users/me')
      .then((me) => {
        if (cancelled) return;
        const activeOrgId = resolveActiveOrgId(me);
        if (activeOrgId != null) {
          window.localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, String(activeOrgId));
          navigate('/', { replace: true });
          return;
        }
        setCheckingExistingOrg(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Setup failed';
        if (message.includes('403') || message.includes('User not registered')) {
          setCheckingExistingOrg(false);
          return;
        }
        setError(message);
        setCheckingExistingOrg(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (checkingExistingOrg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface px-6">
        <span className="material-symbols-outlined animate-spin text-4xl text-primary">
          progress_activity
        </span>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const created = await apiClient<UserProfile>('/setup', {
        method: 'POST',
        body: JSON.stringify({ organization_name: orgName }),
      });
      const activeOrgId = created.organization_id ?? created.organization_ids?.[0];
      if (activeOrgId) {
        window.localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, String(activeOrgId));
      }
      navigate('/', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Setup failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-on-primary text-3xl">domain_add</span>
          </div>
          <h1 className="text-2xl font-headline font-black tracking-tight text-on-surface mb-2">
            Create your organisation
          </h1>
          <p className="text-on-surface-variant text-sm">
            Set up your workspace to start creating CTV campaigns.
          </p>
        </div>

        {/* Card */}
        <div className="bg-surface-container-lowest p-8 rounded-2xl border border-outline-variant/10 ambient-shadow">
          {error && (
            <div className="mb-6 p-3 rounded-lg bg-error-container text-on-error-container text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label
                htmlFor="org-name"
                className="block text-xs font-semibold tracking-wider text-on-surface-variant uppercase ml-1"
              >
                Organisation Name
              </label>
              <input
                id="org-name"
                className="w-full px-4 py-3 bg-surface-container-low border-b-2 border-transparent focus:border-primary rounded-t-lg text-sm text-on-surface outline-none placeholder:text-outline-variant transition-all"
                placeholder="Acme Corp"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
                minLength={1}
                maxLength={200}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !orgName.trim()}
              className="w-full py-4 bg-gradient-to-br from-primary to-primary-dim text-on-primary font-headline font-bold rounded-xl shadow-lg shadow-primary/20 hover:opacity-90 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating…' : 'Create Organisation'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
